/**
 * Zone Details Table - New Implementation
 *
 * Displays zones and their components in an expandable table.
 * Simplified without language dictionary - uses direct field values.
 */
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Collapse,
	Box,
	Chip,
	Typography,
	Button,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Menu,
	MenuItem,
	ListItemIcon,
	Popper,
	Fade,
	ClickAwayListener,
} from "@mui/material";
import {
	KeyboardArrowDown as ExpandMoreIcon,
	KeyboardArrowUp as ExpandLessIcon,
	Edit as EditIcon,
	Delete as DeleteIcon,
	Add as AddIcon,
	Visibility as VisibilityIcon,
	ContentCopy as CloneIcon,
} from "@mui/icons-material";
import {
	addZone,
	updateZone,
	deleteZone,
	addComponent,
	updateComponent,
	deleteComponent,
	cloneZone,
	cloneComponent,
} from "../redux/slice";
import ZoneEditDialog from "./zoneEditDialog.jsx";
import ComponentEditDialog from "./componentEditDialog.jsx";

/**
 * Single zone row with expandable components section
 */
const ZoneRow = ({
	zone,
	zoneIndex,
	components,
	inspectionTypes,
	onEditZone,
	onDeleteZone,
	onAddComponent,
	onEditComponent,
	onDeleteComponent,
	onCloneZone,
	onCloneComponent,
	onZoneHover,
	isHighlighted,
	activeInspectionPopper,
	openInspectionPopper,
	closeInspectionPopper,
	activeConditionPopper,
	openConditionPopper,
	closeConditionPopper,
}) => {
	const [open, setOpen] = useState(false);
	const [zoneContextMenu, setZoneContextMenu] = useState(null);
	const [componentContextMenu, setComponentContextMenu] = useState(null);
	const [componentContextMenuIndex, setComponentContextMenuIndex] = useState(null);
	const [inspectionShowMoreAnchor, setInspectionShowMoreAnchor] = useState(null);
	const [conditionsShowMoreAnchor, setConditionsShowMoreAnchor] = useState(null);
	const [conditionsShowMoreIndex, setConditionsShowMoreIndex] = useState(null);

	const inspectionPopperActive = activeInspectionPopper?.zoneIndex === zoneIndex;
	const inspectionShowMoreOpen = inspectionPopperActive && Boolean(inspectionShowMoreAnchor);

	const conditionsPopperActive =
		activeConditionPopper?.zoneIndex === zoneIndex &&
		activeConditionPopper?.componentIndex === conditionsShowMoreIndex;
	const conditionsShowMoreOpen = conditionsPopperActive && Boolean(conditionsShowMoreAnchor);

	useEffect(() => {
		if (!inspectionPopperActive) {
			setInspectionShowMoreAnchor(null);
		}
	}, [inspectionPopperActive]);

	useEffect(() => {
		if (
			!conditionsPopperActive ||
			activeConditionPopper?.zoneIndex !== zoneIndex ||
			activeConditionPopper?.componentIndex !== conditionsShowMoreIndex
		) {
			setConditionsShowMoreAnchor(null);
			setConditionsShowMoreIndex(null);
		}
	}, [conditionsPopperActive, activeConditionPopper, zoneIndex, conditionsShowMoreIndex]);

	// Zone context menu handlers
	const handleZoneContextMenu = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setZoneContextMenu({
			mouseX: event.clientX - 2,
			mouseY: event.clientY - 4,
		});
	};

	const handleCloseZoneContextMenu = () => {
		setZoneContextMenu(null);
	};

	// Component context menu handlers
	const handleComponentContextMenu = (event, componentIndex) => {
		event.preventDefault();
		event.stopPropagation();
		setComponentContextMenu({
			mouseX: event.clientX - 2,
			mouseY: event.clientY - 4,
		});
		setComponentContextMenuIndex(componentIndex);
	};

	const handleCloseComponentContextMenu = () => {
		setComponentContextMenu(null);
		setComponentContextMenuIndex(null);
	};

	const handleInspectionShowMoreClick = (event) => {
		event.stopPropagation();
		const clickedSameTrigger =
			inspectionPopperActive && inspectionShowMoreAnchor === event.currentTarget;
		if (clickedSameTrigger) {
			handleInspectionShowMoreClose();
			return;
		}
		setInspectionShowMoreAnchor(event.currentTarget);
		openInspectionPopper({ zoneIndex });
	};

	const handleInspectionShowMoreClose = () => {
		setInspectionShowMoreAnchor(null);
		closeInspectionPopper();
	};

	const handleConditionsShowMoreClick = (event, componentIndex) => {
		event.stopPropagation();
		const clickedSameTrigger =
			conditionsPopperActive &&
			conditionsShowMoreIndex === componentIndex &&
			conditionsShowMoreAnchor === event.currentTarget;
		if (clickedSameTrigger) {
			handleConditionsShowMoreClose();
			return;
		}
		setConditionsShowMoreAnchor(event.currentTarget);
		setConditionsShowMoreIndex(componentIndex);
		openConditionPopper({ zoneIndex, componentIndex });
	};

	const handleConditionsShowMoreClose = () => {
		setConditionsShowMoreAnchor(null);
		setConditionsShowMoreIndex(null);
		closeConditionPopper();
	};

	// Get zone name - DIRECT field access
	const getZoneName = () => {
		return zone.tag || `Zone ${zone.tagNumber}`;
	};

	// Get component name - DIRECT field access
	const getComponentName = (component) => {
		return component.component || "Unknown Component";
	};

	// Get condition name - DIRECT array access
	const getConditionName = (condition) => {
		return condition || "Unknown Condition";
	};

	// Get inspection names - DIRECT field access
	// Only show inspection types that exist in the config
	const getInspectionNames = () => {
		if (!zone.inspectionTypes || zone.inspectionTypes.length === 0) {
			return ["None"];
		}

		const names = zone.inspectionTypes
			.map((typeId) => {
				const inspection = inspectionTypes.find(
					(i) => i.typeId === typeId || i.detailType === typeId
				);
				// Only return names for inspection types that exist in the config
				return inspection?.detailType;
			})
			.filter(Boolean); // Remove undefined/null entries (non-existent types)

		if (names.length === 0) {
			return ["None"];
		}

		return names.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
	};

	// Get the actual component objects from the indices
	const zoneComponents = zone.tagComponents
		? zone.tagComponents.map((index) => components[index]).filter(Boolean)
		: [];

	const inspectionNames = getInspectionNames();
	const visibleInspectionNames = inspectionNames.slice(0, 3);
	const hiddenInspectionNames = inspectionNames.slice(3);

	return (
		<>
			<TableRow
				hover
				onMouseEnter={() => onZoneHover?.(zoneIndex)}
				onMouseLeave={() => onZoneHover?.(null)}
				onContextMenu={handleZoneContextMenu}
				sx={{ bgcolor: isHighlighted ? "action.hover" : "inherit", cursor: "context-menu" }}
			>
				<TableCell>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 72 }}>
						<Box
							sx={{ width: 24, display: "flex", alignItems: "center", justifyContent: "center" }}
						>
							{isHighlighted && (
								<Tooltip title="Highlighted in asset view">
									<VisibilityIcon fontSize="small" color="secondary" />
								</Tooltip>
							)}
						</Box>
						<IconButton size="small" onClick={() => setOpen(!open)}>
							{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</IconButton>
					</Box>
				</TableCell>
				<TableCell>
					<Typography variant="body2" fontWeight="medium">
						{getZoneName()}
					</Typography>
				</TableCell>
				<TableCell align="center">{zone.tagNumber}</TableCell>
				<TableCell>
					<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
						{visibleInspectionNames.map((name, idx) => (
							<Chip key={`${name}-${idx}`} label={name} size="small" />
						))}
						{hiddenInspectionNames.length > 0 && (
							<>
								<Chip
									label={`+${hiddenInspectionNames.length} more`}
									size="small"
									variant="outlined"
									onClick={handleInspectionShowMoreClick}
									sx={{ cursor: "pointer" }}
									style={{ backgroundColor: 'transparent' }}
								/>
								<Popper
									open={inspectionShowMoreOpen}
									anchorEl={inspectionShowMoreAnchor}
									placement="right-start"
									transition
								>
									{({ TransitionProps }) => (
										<Fade {...TransitionProps} timeout={200}>
											<Paper elevation={3}>
												<ClickAwayListener onClickAway={handleInspectionShowMoreClose}>
													<Box sx={{ p: 1, maxWidth: 240, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
														{hiddenInspectionNames.map((name, hiddenIdx) => (
															<Chip key={`${name}-${hiddenIdx}`} label={name} size="small" />
														))}
													</Box>
												</ClickAwayListener>
											</Paper>
										</Fade>
									)}
								</Popper>
							</>
						)}
					</Box>
				</TableCell>
				<TableCell align="right">
					<Tooltip title="Edit zone">
						<IconButton size="small" onClick={() => onEditZone(zoneIndex)}>
							<EditIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Delete zone">
						<IconButton size="small" onClick={() => onDeleteZone(zoneIndex)}>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Box sx={{ margin: 2 }}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 1,
								}}
							>
								<Typography variant="subtitle2" component="div" sx={{ fontWeight: "bold" }}>
									Components ({zoneComponents.length})
								</Typography>
								<Button
									size="small"
									startIcon={<AddIcon />}
									onClick={() => onAddComponent(zoneIndex)}
									variant="outlined"
								>
									Add Component
								</Button>
							</Box>
							{zoneComponents.length === 0 ? (
								<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
									No components assigned to this zone
								</Typography>
							) : (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Component Name</TableCell>
											<TableCell>Conditions</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{zoneComponents.map((component, idx) => (
											<TableRow
												key={idx}
												onContextMenu={(e) => handleComponentContextMenu(e, idx)}
												sx={{ cursor: "context-menu" }}
											>
												<TableCell component="th" scope="row">
													{getComponentName(component)}
												</TableCell>
												<TableCell>
													<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
														{(component.conditions || []).slice(0, 5).map((condition, conditionIdx) => (
															<Chip
																key={`${getConditionName(condition)}-${conditionIdx}`}
																label={getConditionName(condition)}
																size="small"
																variant="outlined"
															/>
														))}
														{(component.conditions?.length || 0) > 5 && (
															<>
																<Chip
																	label={`+${component.conditions.length - 5} more`}
																	size="small"
																	variant="outlined"
																	sx={{ cursor: "pointer" }}
																	onClick={(event) => handleConditionsShowMoreClick(event, idx)}
																	style={{ backgroundColor: 'transparent' }}
																/>
																<Popper
																	open={conditionsShowMoreOpen && conditionsShowMoreIndex === idx}
																	anchorEl={conditionsShowMoreAnchor}
																	placement="right-start"
																	transition
																>
																	{({ TransitionProps }) => (
																		<Fade {...TransitionProps} timeout={200}>
																			<Paper elevation={3}>
																				<ClickAwayListener onClickAway={handleConditionsShowMoreClose}>
																					<Box sx={{ p: 1, maxWidth: 240, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
																						{component.conditions.slice(5).map((condition, hiddenIdx) => (
																							<Chip
																								key={`${getConditionName(condition)}-${hiddenIdx}`}
																								label={getConditionName(condition)}
																								size="small"
																								variant="outlined"
																							/>
																						))}
																					</Box>
																				</ClickAwayListener>
																			</Paper>
																		</Fade>
																	)}
																</Popper>
															</>
														)}
													</Box>
												</TableCell>
												<TableCell align="right">
													<Box sx={{ display: "flex", flexWrap: "nowrap", justifyContent: "flex-end" }}>
														<Tooltip title="Edit component">
															<IconButton
																size="small"
																onClick={() => onEditComponent(zoneIndex, idx)}
															>
																<EditIcon fontSize="small" />
															</IconButton>
														</Tooltip>
														<Tooltip title="Delete component">
															<IconButton
																size="small"
																onClick={() => onDeleteComponent(zoneIndex, idx)}
															>
																<DeleteIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													</Box>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>

			{/* Zone Context Menu */}
			<Menu
				open={zoneContextMenu !== null}
				onClose={handleCloseZoneContextMenu}
				anchorReference="anchorPosition"
				anchorPosition={
					zoneContextMenu !== null
						? { top: zoneContextMenu.mouseY, left: zoneContextMenu.mouseX }
						: undefined
				}
			>
				<MenuItem
					onClick={() => {
						onEditZone(zoneIndex);
						handleCloseZoneContextMenu();
					}}
				>
					<ListItemIcon>
						<EditIcon fontSize="small" />
					</ListItemIcon>
					Edit
				</MenuItem>
				<MenuItem
					onClick={() => {
						onCloneZone(zoneIndex);
						handleCloseZoneContextMenu();
					}}
				>
					<ListItemIcon>
						<CloneIcon fontSize="small" />
					</ListItemIcon>
					Clone below
				</MenuItem>
				<MenuItem
					onClick={() => {
						onDeleteZone(zoneIndex);
						handleCloseZoneContextMenu();
					}}
				>
					<ListItemIcon>
						<DeleteIcon fontSize="small" />
					</ListItemIcon>
					Delete
				</MenuItem>
			</Menu>

			{/* Component Context Menu */}
			<Menu
				open={componentContextMenu !== null}
				onClose={handleCloseComponentContextMenu}
				anchorReference="anchorPosition"
				anchorPosition={
					componentContextMenu !== null
						? { top: componentContextMenu.mouseY, left: componentContextMenu.mouseX }
						: undefined
				}
			>
				<MenuItem
					onClick={() => {
						onEditComponent(zoneIndex, componentContextMenuIndex);
						handleCloseComponentContextMenu();
					}}
				>
					<ListItemIcon>
						<EditIcon fontSize="small" />
					</ListItemIcon>
					Edit
				</MenuItem>
				<MenuItem
					onClick={() => {
						onCloneComponent(zoneIndex, componentContextMenuIndex);
						handleCloseComponentContextMenu();
					}}
				>
					<ListItemIcon>
						<CloneIcon fontSize="small" />
					</ListItemIcon>
					Clone below
				</MenuItem>
				<MenuItem
					onClick={() => {
						onDeleteComponent(zoneIndex, componentContextMenuIndex);
						handleCloseComponentContextMenu();
					}}
				>
					<ListItemIcon>
						<DeleteIcon fontSize="small" />
					</ListItemIcon>
					Delete
				</MenuItem>
			</Menu>
		</>
	);
};

/**
 * Main Zone Details Table component
 */
const ZoneDetailsTable = ({
	layout,
	layoutIndex,
	inspectionTypes,
	onZoneHover,
	highlightedZoneIndex,
}) => {
	const dispatch = useDispatch();
	const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
	const [componentDialogOpen, setComponentDialogOpen] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [editingZone, setEditingZone] = useState(null);
	const [editingZoneIndex, setEditingZoneIndex] = useState(null);
	const [editingComponent, setEditingComponent] = useState(null);
	const [editingComponentIndex, setEditingComponentIndex] = useState(null);
	const [editingComponentZoneIndex, setEditingComponentZoneIndex] = useState(null);
	const [activeInspectionPopper, setActiveInspectionPopper] = useState(null);
	const [activeConditionPopper, setActiveConditionPopper] = useState(null);

	const openInspectionPopper = (target) => {
		setActiveConditionPopper(null);
		setActiveInspectionPopper(target);
	};

	const closeInspectionPopper = () => {
		setActiveInspectionPopper(null);
	};

	const openConditionPopper = (target) => {
		setActiveInspectionPopper(null);
		setActiveConditionPopper(target);
	};

	const closeConditionPopper = () => {
		setActiveConditionPopper(null);
	};

	const handleAddZone = () => {
		setEditingZone(null);
		setEditingZoneIndex(null);
		setZoneDialogOpen(true);
	};

	const handleEditZone = (zoneIndex) => {
		setEditingZone(layout.configZones[zoneIndex]);
		setEditingZoneIndex(zoneIndex);
		setZoneDialogOpen(true);
	};

	const handleDeleteZone = (zoneIndex) => {
		setDeleteTarget({ type: "zone", zoneIndex });
		setDeleteConfirmOpen(true);
	};

	const handleAddComponent = (zoneIndex) => {
		setEditingComponent(null);
		setEditingComponentIndex(null);
		setEditingComponentZoneIndex(zoneIndex);
		setComponentDialogOpen(true);
	};

	const handleEditComponent = (zoneIndex, componentIndex) => {
		const zone = layout.configZones[zoneIndex];
		const componentGlobalIndex = zone.tagComponents[componentIndex];
		const component = layout.components[componentGlobalIndex];

		setEditingComponent(component);
		setEditingComponentIndex(componentGlobalIndex);
		setEditingComponentZoneIndex(zoneIndex);
		setComponentDialogOpen(true);
	};

	const handleDeleteComponent = (zoneIndex, componentIndex) => {
		const zone = layout.configZones[zoneIndex];
		const componentGlobalIndex = zone.tagComponents[componentIndex];
		setDeleteTarget({ type: "component", zoneIndex, componentIndex: componentGlobalIndex });
		setDeleteConfirmOpen(true);
	};

	// Clone handlers
	const handleCloneZone = (zoneIndex) => {
		dispatch(cloneZone({ layoutIndex, zoneIndex }));
	};

	const handleCloneComponent = (zoneIndex, tagComponentIndex) => {
		dispatch(cloneComponent({ layoutIndex, zoneIndex, tagComponentIndex }));
	};

	const confirmDelete = () => {
		if (deleteTarget.type === "zone") {
			dispatch(deleteZone({ layoutIndex, zoneIndex: deleteTarget.zoneIndex }));
		} else if (deleteTarget.type === "component") {
			dispatch(
				deleteComponent({
					layoutIndex,
					zoneIndex: deleteTarget.zoneIndex,
					componentIndex: deleteTarget.componentIndex,
				})
			);
		}
		setDeleteConfirmOpen(false);
		setDeleteTarget(null);
	};

	const handleSaveZone = (zoneData) => {
		if (editingZoneIndex === null) {
			// Adding new zone - use direct field values
			const newZone = {
				tag: zoneData.tag,
				tagType: zoneData.tagType,
				tagNumber: parseInt(zoneData.tagNumber, 10),
				assetViewLocation: zoneData.assetViewLocation,
				tagComponents: [],
				inspectionTypes: zoneData.inspectionTypes || [],
			};
			dispatch(addZone({ layoutIndex, zone: newZone }));
		} else {
			// Updating existing zone
			const updatedZone = {
				...layout.configZones[editingZoneIndex],
				tag: zoneData.tag,
				tagType: zoneData.tagType,
				tagNumber: parseInt(zoneData.tagNumber, 10),
				assetViewLocation: zoneData.assetViewLocation,
				inspectionTypes: zoneData.inspectionTypes || [],
			};
			dispatch(updateZone({ layoutIndex, zoneIndex: editingZoneIndex, zone: updatedZone }));
		}

		setZoneDialogOpen(false);
		setEditingZone(null);
		setEditingZoneIndex(null);
	};

	const handleSaveComponent = (componentData) => {
		if (editingComponentIndex === null) {
			// Adding new component - use direct field values
			// Use default severity values (hidden from UI but required for backend)
			const newComponent = {
				component: componentData.component,
				minSeverity: 0,
				maxSeverity: 255,
				conditions: componentData.conditions || [],
			};
			dispatch(
				addComponent({
					layoutIndex,
					zoneIndex: editingComponentZoneIndex,
					component: newComponent,
				})
			);
		} else {
			// Updating existing component - preserve existing severity values
			const existingComponent = layout.components[editingComponentIndex];
			const updatedComponent = {
				...existingComponent,
				component: componentData.component,
				minSeverity: existingComponent?.minSeverity ?? 0,
				maxSeverity: existingComponent?.maxSeverity ?? 255,
				conditions: componentData.conditions || [],
			};
			dispatch(
				updateComponent({
					layoutIndex,
					componentIndex: editingComponentIndex,
					component: updatedComponent,
				})
			);
		}

		setComponentDialogOpen(false);
		setEditingComponent(null);
		setEditingComponentIndex(null);
		setEditingComponentZoneIndex(null);
	};

	if (!layout || !layout.configZones) {
		return (
			<Box sx={{ p: 3, textAlign: "center" }}>
				<Typography variant="body1" color="text.secondary">
					Select a layout to view zone details
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ mt: 3 }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
				<Typography variant="h6">Zone Details ({layout.configZones.length} zones)</Typography>
				<Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddZone}>
					Add Zone
				</Button>
			</Box>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell width={50} />
							<TableCell>Zone Name</TableCell>
							<TableCell align="center">Zone #</TableCell>
							<TableCell>Used In Inspections</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{layout.configZones.map((zone, index) => (
							<ZoneRow
								key={index}
								zone={zone}
								zoneIndex={index}
								layoutIndex={layoutIndex}
								components={layout.components || []}
								inspectionTypes={inspectionTypes}
								onEditZone={handleEditZone}
								onDeleteZone={handleDeleteZone}
								onAddComponent={handleAddComponent}
								onEditComponent={handleEditComponent}
								onDeleteComponent={handleDeleteComponent}
								onCloneZone={handleCloneZone}
								onCloneComponent={handleCloneComponent}
								onZoneHover={onZoneHover}
								isHighlighted={Array.isArray(highlightedZoneIndex) ? highlightedZoneIndex.includes(index) : highlightedZoneIndex === index}
								activeInspectionPopper={activeInspectionPopper}
								openInspectionPopper={openInspectionPopper}
								closeInspectionPopper={closeInspectionPopper}
								activeConditionPopper={activeConditionPopper}
								openConditionPopper={openConditionPopper}
								closeConditionPopper={closeConditionPopper}
							/>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<ZoneEditDialog
				open={zoneDialogOpen}
				zone={editingZone}
				existingZones={layout.configZones || []}
				layout={layout}
				inspectionTypes={inspectionTypes}
				onClose={() => {
					setZoneDialogOpen(false);
					setEditingZone(null);
					setEditingZoneIndex(null);
				}}
				onSave={handleSaveZone}
			/>

			<ComponentEditDialog
				open={componentDialogOpen}
				component={editingComponent}
				onClose={() => {
					setComponentDialogOpen(false);
					setEditingComponent(null);
					setEditingComponentIndex(null);
					setEditingComponentZoneIndex(null);
				}}
				onSave={handleSaveComponent}
			/>

			<Dialog
				open={deleteConfirmOpen}
				onClose={() => {
					setDeleteConfirmOpen(false);
					setDeleteTarget(null);
				}}
			>
				<DialogTitle>Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete this{" "}
						{deleteTarget?.type === "zone" ? "zone" : "component"}? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setDeleteConfirmOpen(false);
							setDeleteTarget(null);
						}}
					>
						Cancel
					</Button>
					<Button onClick={confirmDelete} color="error" variant="contained">
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ZoneDetailsTable;
